import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * CDR STRICT EXCEL ANALYZER — FULL MERGED VERSION (TUNED)
 * ✅ Keeps ALL existing features:
 *   - Strict operator templates (exact headers + exact count)
 *   - Upload + raw preview always visible
 *   - Filters (date range, IMEI contains, B-party contains)
 *   - Top 10 / Top 20 toggle
 *   - Grand Totals footer rows (UI)
 *   - Export Excel + PDF
 *   - Downloadable error report + Copy
 * ✅ Improvements added (no breaking changes):
 *   - Stronger MSISDN normalization (handles 0092 / 92 / 03 variants)
 *   - B-Party classification: MSISDN vs ShortCodes vs Invalid/Other
 *   - MFC contacts dedupe via canonical bParty
 *   - New "Short Codes" tab + Excel sheet
 *   - PDF respects TopN + totals shown once (showFoot: "lastPage") + section headings
 */

const PATTERNS = {
  Zong: [
    "CALL_TYPE","MSISDN","STRT_TM","BNUMBER","MINS","SECS",
    "LAC_ID","CELL_ID","IMEI","SITE_ADDRESS","LNG","LAT",
  ],
  Jazz: [
    "CallType","Aparty","BParty","Datetime","Duration","cellid","Imsi","Imei",
    "SiteLocation","Longitude and Latitude","Node ID","IP","Port",
  ],
  Ufone: [
    "IMEI","IMSI","A Number","B Number","Start Time","End Time","Service Provider",
    "Type","Direction","Location","Cell Id","Cell Sector","Latitude","Longitude","Duration",
  ],
  Telenor: [
    "MSISDN","call_org_num","CALL_DIALED_NUM","IMSI","IMEI",
    "CALL_START_DT_TM","CALL_END_DT_TM","INBOUND_OUTBOUND_IND","Call_Network_Volume",
    "Lac_Id","Site_Id","Cell_SITE_ID","lat","longitude","CALL_TYPE","location",
  ],
};

const PREFIX_RULES = {
  Jazz: ["030", "032"],
  Zong: ["031", "037"],
  Telenor: ["034"],
  Ufone: ["033"],
};

const styles = {
  page: { padding: 16, fontFamily: "system-ui, Arial, sans-serif" },
  headingMain: { fontSize: 30, lineHeight: 1.2, margin: "0 0 8px" },
  headingSection: { fontSize: 22, lineHeight: 1.2, margin: "0 0 8px" },
  
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    background: "#fff",
  },
  row: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },
  btn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #111827",
    background: "#111827",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 800,
  },
  btnGhost: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    cursor: "pointer",
    fontWeight: 800,
  },
  btnDanger: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #b91c1c",
    background: "#b91c1c",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 900,
  },
  badge: {
    display: "inline-flex",
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    fontSize: 12,
    fontWeight: 900,
  },
  tabs: { display: "flex", gap: 8, flexWrap: "wrap" },
  modeTabs: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 },
  modeBtn: (active) => ({
    padding: "10px 14px",
    borderRadius: 10,
    border: active ? "1px solid #111827" : "1px solid #d1d5db",
    background: active ? "#111827" : "#fff",
    color: active ? "#fff" : "#111827",
    cursor: "pointer",
    fontWeight: 900,
  }),
  tab: (active) => ({
    padding: "8px 12px",
    borderRadius: 10,
    border: active ? "1px solid #111827" : "1px solid #d1d5db",
    background: active ? "#111827" : "#fff",
    color: active ? "#fff" : "#111827",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
  }),
  tableWrap: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: 520,
    background: "#ffffff"
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, color: "#111827" },
  th: {
    position: "sticky",
    top: 0,
    background: "#111827",
    color:"#ffffff",
    borderBottom: "1px solid #e5e7eb",
    padding: "10px 8px",
    textAlign: "left",
    whiteSpace: "nowrap",
   
  },
  rawTd:{
    fontFamily:'monospace',
     color:"#111827",
    background:"#ffffff"
  }
  ,
  td: {
    borderBottom: "1px solid #ddd5d4ff",
    padding: "8px 8px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
    color:"#111827",
    background:"#ffffff"
  },
  tfoot: { background: "#f8fafc", borderTop: "2px solid #e5e7eb", fontWeight: 900 },
  muted: { color: "#9ca3af", fontSize: 13 },
  danger: { color: "#b91c1c", fontSize: 13, fontWeight: 900 },
  ok: { color: "#065f46", fontSize: 13, fontWeight: 900 },
  warnBox: { border: "1px solid #fecaca", background: "#fff1f2", padding: 12, borderRadius: 12 },
  field: { border: "1px solid #060708ff", borderRadius: 10, padding: "10px 12px", minWidth: 200 },
  label: { fontSize: 12, fontWeight: 900, marginBottom: 6 },
  input: { width: "100%", border: "none", outline: "none", fontSize: 14, background: "transparent" },
  centerWrap: {
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};

const cardSx = {
  p: 2,
  mb: 1.5,
  borderRadius: 3,
  border: 1,
  borderColor: "divider",
  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.08)",
};

const btnBaseSx = { textTransform: "none", borderRadius: 2, fontWeight: 700 };

function SurfaceCard({ children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, ...sx }}>
      {children}
    </Paper>
  );
}

function AppButton({ tone = "primary", sx, ...props }) {
  if (tone === "ghost") {
    return (
      <Button
        size="small"
        variant="outlined"
        sx={{ ...btnBaseSx, borderColor: "divider", color: "text.primary", ...sx }}
        {...props}
      />
    );
  }
  if (tone === "danger") {
    return <Button size="small" variant="contained" color="error" sx={{ ...btnBaseSx, ...sx }} {...props} />;
  }
  return <Button size="small" variant="contained" sx={{ ...btnBaseSx, ...sx }} {...props} />;
}

function AppBadge({ label }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      sx={{ fontWeight: 700, borderColor: "divider", bgcolor: "action.hover", color: "text.primary" }}
    />
  );
}

const EXCEL_EXTENSIONS = [".xlsx", ".xls", ".xlsm", ".xlsb"];
const CSV_EXTENSION = ".csv";
const SUPPORTED_DATASET_EXTENSIONS = [...EXCEL_EXTENSIONS, CSV_EXTENSION];
const EXCEL_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
];
const EXCEL_ACCEPT = [...SUPPORTED_DATASET_EXTENSIONS, ...EXCEL_MIME_TYPES, "text/csv"].join(",");
const SCI_ID_COLUMNS = [
  "MSISDN",
  "call_org_num",
  "CALL_DIALED_NUM",
  "call_dialed_num",
  "BNUMBER",
  "Aparty",
  "BParty",
  "A Number",
  "B Number",
  "IMSI",
  "Imsi",
  "IMEI",
  "Imei",
];

function isSupportedDatasetFile(file) {
  if (!file) return false;
  const name = toStr(file.name).toLowerCase();
  const byExtension = SUPPORTED_DATASET_EXTENSIONS.some((ext) => name.endsWith(ext));
  const byMime = file.type ? EXCEL_MIME_TYPES.includes(file.type) || file.type === "text/csv" : false;
  return byExtension || byMime;
}

function isScientificNotationString(value) {
  return /^[+-]?\d+(?:\.\d+)?e[+-]?\d+$/i.test(toStr(value));
}

function expandScientificToPlain(value) {
  const input = toStr(value);
  if (!isScientificNotationString(input)) return input;

  const sign = input.startsWith("-") ? "-" : "";
  const unsigned = input.replace(/^[+-]/, "");
  const [mantissa, expRaw] = unsigned.toLowerCase().split("e");
  const exponent = parseInt(expRaw, 10);
  if (!Number.isFinite(exponent)) return input;

  const dotIndex = mantissa.indexOf(".");
  const digits = mantissa.replace(".", "");
  const decimalIndex = dotIndex === -1 ? mantissa.length : dotIndex;
  const targetIndex = decimalIndex + exponent;

  if (targetIndex <= 0) {
    const out = `0.${"0".repeat(Math.abs(targetIndex))}${digits}`;
    return `${sign}${out}`;
  }
  if (targetIndex >= digits.length) {
    const out = `${digits}${"0".repeat(targetIndex - digits.length)}`;
    return `${sign}${out}`;
  }

  const out = `${digits.slice(0, targetIndex)}.${digits.slice(targetIndex)}`;
  return `${sign}${out}`;
}

function normalizeIdentifierValue(value) {
  if (value === null || value === undefined || value === "") return value;

  if (typeof value === "number" && Number.isFinite(value)) {
    // Telecom IDs are integer-like. Avoid scientific notation in preview/analysis.
    return Number.isInteger(value)
      ? value.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: 0 })
      : value.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: 20 });
  }

  const text = toStr(value);
  if (!text) return value;

  const expanded = isScientificNotationString(text) ? expandScientificToPlain(text) : text;
  if (/^-?\d+\.0+$/.test(expanded)) return expanded.replace(/\.0+$/, "");

  return expanded;
}

function normalizeDigitsOnlyIdentifier(value) {
  const raw = toStr(value);
  if (!raw) return "";

  const normalized = toStr(normalizeIdentifierValue(raw));
  if (!normalized) return "";
  const digits = normalized.replace(/\D/g, "");
  return digits || normalized;
}

function isLowPrecisionScientific(value) {
  const text = toStr(value);
  if (!isScientificNotationString(text)) return false;
  const [mantissa] = text.toLowerCase().split("e");
  const significantDigits = mantissa.replace(/[^0-9]/g, "").replace(/^0+/, "").length;
  return significantDigits > 0 && significantDigits <= 7;
}

function getScientificWarningInfo(rows, headers) {
  if (!Array.isArray(rows) || !rows.length || !Array.isArray(headers) || !headers.length) return null;
  const targetCols = headers.filter((h) => SCI_ID_COLUMNS.includes(h));
  if (!targetCols.length) return null;

  for (const col of targetCols) {
    for (let i = 0; i < rows.length && i < 400; i += 1) {
      const v = rows[i]?.[col];
      if (isLowPrecisionScientific(v)) {
        return { column: col, sample: toStr(v) };
      }
    }
  }
  return null;
}

function getExtensionFromPath(filePath) {
  const normalized = toStr(filePath).toLowerCase().split(/[?#]/)[0];
  const lastDot = normalized.lastIndexOf(".");
  return lastDot >= 0 ? normalized.slice(lastDot) : "";
}

function ensureSupportedExtension(extension) {
  if (SUPPORTED_DATASET_EXTENSIONS.includes(extension)) return;
  const allowed = SUPPORTED_DATASET_EXTENSIONS.join(", ");
  const shown = extension || "(none)";
  throw new Error(`Unsupported file type: ${shown}. Supported types: ${allowed}`);
}

function parseCsvMatrix(csvText) {
  const text = toStr(csvText).replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === "\"") {
      if (inQuotes && text[i + 1] === "\"") {
        cell += "\"";
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }
    if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += ch;
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows;
}

function matrixToDataset(matrix) {
  if (!Array.isArray(matrix) || matrix.length === 0) return { rows: [], headers: [] };
  const headers = (matrix[0] || []).map((h) => toStr(h));
  if (!headers.length) return { rows: [], headers: [] };

  const rows = matrix.slice(1).map((line) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = line?.[idx] ?? "";
    });
    return obj;
  });
  return { rows, headers };
}

function parseCsvDataset(csvText) {
  return matrixToDataset(parseCsvMatrix(csvText));
}

function parseExcelDataset(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const firstSheetName = wb.SheetNames?.[0];
  if (!firstSheetName) return { rows: [], headers: [] };
  const ws = wb.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
  let headers = rows.length ? Object.keys(rows[0]) : [];

  if (!headers.length) {
    const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    headers = Array.isArray(matrix?.[0]) ? matrix[0].map((h) => toStr(h)) : [];
  }
  return { rows, headers };
}

async function parseDatasetByExtension(extension, readText, readArrayBuffer) {
  ensureSupportedExtension(extension);
  if (extension === CSV_EXTENSION) {
    const text = await readText();
    return parseCsvDataset(text);
  }
  const buffer = await readArrayBuffer();
  return parseExcelDataset(buffer);
}

async function loadDatasetFromFile(file) {
  if (!file) throw new Error("No file provided.");
  const extension = getExtensionFromPath(file.name);
  return parseDatasetByExtension(
    extension,
    () => file.text(),
    () => file.arrayBuffer()
  );
}

/**
 * Loads a dataset from a path/URL and returns one unified table structure.
 * Returns: { rows: Array<Record<string, string|number>>, headers: string[] }
 */
export async function loadDatasetFromPath(filePath) {
  const path = toStr(filePath);
  if (!path) throw new Error("File path is required.");

  const extension = getExtensionFromPath(path);
  ensureSupportedExtension(extension);

  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to load file from path: ${path} (HTTP ${response.status})`);
  }

  try {
    return await parseDatasetByExtension(
      extension,
      () => response.text(),
      () => response.arrayBuffer()
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown parsing error";
    throw new Error(`Unable to parse file "${path}": ${message}`);
  }
}

function toStr(v) {
  if (v === undefined || v === null) return "";
  return String(v).trim();
}

/**
 * ✅ Stronger canonical MSISDN normalization (local PK format)
 * - 0092xxxxxxxxxx -> 03xxxxxxxxx
 * - 92xxxxxxxxxx   -> 03xxxxxxxxx
 * - 3xxxxxxxxx     -> 03xxxxxxxxx
 * - keeps 03xxxxxxxxx unchanged
 */
function normalizeMSISDN(x) {
  const raw = toStr(x);
  const source = normalizeIdentifierValue(raw);
  const n = toStr(source).replace(/\D/g, "");
  if (!n) return "";

  if (n.startsWith("0092") && n.length >= 14) return "0" + n.slice(4);
  if (n.startsWith("92") && n.length >= 12) return "0" + n.slice(2);

  if (n.startsWith("0") && n.length === 11) return n;
  if (n.startsWith("3") && n.length === 10) return "0" + n;

  return n;
}

function guessOperatorByPrefix(msisdn) {
  const p = normalizeMSISDN(msisdn);
  const prefix3 = p.slice(0, 3);
  if (PREFIX_RULES.Jazz.includes(prefix3)) return "Jazz";
  if (PREFIX_RULES.Zong.includes(prefix3)) return "Zong";
  if (PREFIX_RULES.Telenor.includes(prefix3)) return "Telenor";
  if (PREFIX_RULES.Ufone.includes(prefix3)) return "Ufone";
  return "Unknown";
}

function computeHeaderDiff(actualHeaders, expectedHeaders) {
  const actualSet = new Set(actualHeaders);
  const expectedSet = new Set(expectedHeaders);
  const missing = expectedHeaders.filter((h) => !actualSet.has(h));
  const extra = actualHeaders.filter((h) => !expectedSet.has(h));
  return { missing, extra };
}

function validateAgainstPattern(actualHeaders, company) {
  const expected = PATTERNS[company];
  const actualCount = actualHeaders.length;
  const expectedCount = expected.length;
  const { missing, extra } = computeHeaderDiff(actualHeaders, expected);
  const ok = missing.length === 0 && extra.length === 0 && actualCount === expectedCount;
  return { ok, company, expectedCount, actualCount, missing, extra, expected, actualHeaders };
}

function detectCompanyByHeaders(actualHeaders) {
  const results = Object.keys(PATTERNS).map((c) => validateAgainstPattern(actualHeaders, c));
  const perfect = results.find((r) => r.ok);
  if (perfect) return { detected: perfect.company, results };
  const scored = results
    .map((r) => ({ ...r, score: r.missing.length * 3 + r.extra.length * 2 + Math.abs(r.actualCount - r.expectedCount) }))
    .sort((a, b) => a.score - b.score);
  return { detected: scored[0]?.company || "Unknown", results: scored };
}

function parseExcelDate(value) {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === "number") {
    const d = XLSX.SSF.parse_date_code(value);
    if (!d) return null;
    return new Date(d.y, d.m - 1, d.d, d.H, d.M, d.S);
  }
  if (typeof value === "string") {
    const t = Date.parse(value.trim());
    if (!Number.isNaN(t)) return new Date(t);
  }
  return null;
}

function parseDurationSeconds(v) {
  if (!v) return 0;
  if (typeof v === "number") return Math.max(0, Math.floor(v));
  const s = String(v).trim();
  const parts = s.split(":").map((x) => x.trim());
  if (parts.length === 3) {
    const [hh, mm, ss] = parts.map((x) => parseInt(x, 10));
    return (Number(hh) || 0) * 3600 + (Number(mm) || 0) * 60 + (Number(ss) || 0);
  }
  const n = Number(s);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/** ✅ FULL detailed mapping restored */
function buildNormalizedRowsByCompany(rawRows, company) {
  if (company === "Zong") {
    return rawRows.map((r, idx) => ({
      __id: idx,
      aParty: normalizeMSISDN(r["MSISDN"]),
      bParty: normalizeMSISDN(r["BNUMBER"]),
      callType: toStr(r["CALL_TYPE"]),
      dateTime: parseExcelDate(r["STRT_TM"]),
      durationSec: (Number(r["MINS"]) || 0) * 60 + (Number(r["SECS"]) || 0),
      location: toStr(r["SITE_ADDRESS"]),
      lac: toStr(r["LAC_ID"]),
      cellId: toStr(r["CELL_ID"]),
      imei: normalizeDigitsOnlyIdentifier(r["IMEI"]),
      lng: toStr(r["LNG"]),
      lat: toStr(r["LAT"]),
      raw: r,
    }));
  }

  if (company === "Jazz") {
    return rawRows.map((r, idx) => ({
      __id: idx,
      aParty: normalizeMSISDN(r["Aparty"]),
      bParty: normalizeMSISDN(r["BParty"]),
      callType: toStr(r["CallType"]),
      dateTime: parseExcelDate(r["Datetime"]),
      durationSec: parseDurationSeconds(r["Duration"]),
      cellId: toStr(r["cellid"]),
      imsi: normalizeDigitsOnlyIdentifier(r["Imsi"]),
      imei: normalizeDigitsOnlyIdentifier(r["Imei"]),
      siteLocation: toStr(r["SiteLocation"]),
      longLat: toStr(r["Longitude and Latitude"]),
      nodeId: toStr(r["Node ID"]),
      ip: toStr(r["IP"]),
      port: toStr(r["Port"]),
      // for uniform analysis
      location: toStr(r["SiteLocation"]),
      raw: r,
    }));
  }

  if (company === "Ufone") {
    return rawRows.map((r, idx) => ({
      __id: idx,
      aParty: normalizeMSISDN(r["A Number"]),
      bParty: normalizeMSISDN(r["B Number"]),
      callType: `${toStr(r["Type"])} ${toStr(r["Direction"])}`.trim(),
      start: parseExcelDate(r["Start Time"]),
      end: parseExcelDate(r["End Time"]),
      dateTime: parseExcelDate(r["Start Time"]),
      durationSec: parseDurationSeconds(r["Duration"]),
      location: toStr(r["Location"]),
      cellId: toStr(r["Cell Id"]),
      cellSector: toStr(r["Cell Sector"]),
      imei: normalizeDigitsOnlyIdentifier(r["IMEI"]),
      imsi: normalizeDigitsOnlyIdentifier(r["IMSI"]),
      lat: toStr(r["Latitude"]),
      lng: toStr(r["Longitude"]),
      provider: toStr(r["Service Provider"]),
      raw: r,
    }));
  }

  if (company === "Telenor") {
    return rawRows.map((r, idx) => ({
      __id: idx,
      aParty: normalizeMSISDN(r["call_org_num"] || r["MSISDN"]),
      bParty: normalizeMSISDN(r["CALL_DIALED_NUM"]),
      callType: `${toStr(r["CALL_TYPE"])} ${toStr(r["INBOUND_OUTBOUND_IND"])}`.trim(),
      start: parseExcelDate(r["CALL_START_DT_TM"]),
      end: parseExcelDate(r["CALL_END_DT_TM"]),
      dateTime: parseExcelDate(r["CALL_START_DT_TM"]),
      durationSec: parseDurationSeconds(r["Call_Network_Volume"]),
      location: toStr(r["location"]),
      lac: toStr(r["Lac_Id"]),
      siteId: toStr(r["Site_Id"]),
      cellSiteId: toStr(r["Cell_SITE_ID"]),
      imei: normalizeDigitsOnlyIdentifier(r["IMEI"]),
      imsi: normalizeDigitsOnlyIdentifier(r["IMSI"]),
      lat: toStr(r["lat"]),
      lng: toStr(r["longitude"]),
      inboundOutbound: toStr(r["INBOUND_OUTBOUND_IND"]),
      raw: r,
    }));
  }

  return rawRows.map((r, idx) => ({ __id: idx, raw: r }));
}

function classifyDirectionEvent(callTypeText) {
  const t = toStr(callTypeText).toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  const tokens = t ? t.split(" ") : [];
  const hasIncomingWord = /\b(incoming|inbound)\b/.test(t);
  const hasOutgoingWord = /\b(outgoing|outbound)\b/.test(t);
  const incomingTokens = new Set(["in", "mt", "terminating", "received", "receive"]);
  const outgoingTokens = new Set(["out", "mo", "originating", "dialed", "sent", "send"]);
  const hasIncomingToken = tokens.some((x) => incomingTokens.has(x));
  const hasOutgoingToken = tokens.some((x) => outgoingTokens.has(x));
  const isIncoming = hasIncomingWord || hasIncomingToken;
  const isOutgoing = hasOutgoingWord || hasOutgoingToken;
  const isSms = t.includes("sms");
  const isCall = t.includes("call") || !isSms;
  return {
    direction: isOutgoing ? "OUT" : isIncoming ? "IN" : null,
    event: isSms ? "SMS" : isCall ? "CALL" : "UNKNOWN",
  };
}

/**
 * ✅ Classify B-party to avoid MFC pollution + dedupe by canonical key
 */
function classifyBParty(rawBParty) {
  const digits = toStr(rawBParty).replace(/\D/g, "");
  if (!digits) return { type: "BLANK", key: "(blank)", canonical: "" };

  // service IDs / shortcodes (3–8 digits typical)
  if (digits.length <= 8) return { type: "SHORTCODE", key: digits, canonical: digits };

  // canonicalize as PK local number if possible
  const canon = normalizeMSISDN(digits);

  // corruption/concatenation guard
  if (canon.length > 20) return { type: "INVALID", key: digits, canonical: digits };

  // proper MSISDN in local PK form
  if (canon.startsWith("0") && canon.length === 11) return { type: "MSISDN", key: canon, canonical: canon };

  return { type: "OTHER", key: canon, canonical: canon };
}

/**
 * ✅ Improved analysis:
 * - MFC uses canonical bParty (dedupe)
 * - ShortCodes separated
 * - invalid/other separated (kept for future; not shown by default)
 * - MFL/IMEI/IMEI Scan unchanged
 */
function analyzeCDR(normRows) {
  const mfcMap = new Map();
  const shortCodeMap = new Map();
  const invalidMap = new Map();

  const mflMap = new Map();
  const imeiMap = new Map();
  const imeiScanMap = new Map();

  const bump = (map, key, event, direction) => {
    if (!map.has(key)) map.set(key, { bParty: key, inCall: 0, outCall: 0, inSms: 0, outSms: 0, total: 0 });
    const row = map.get(key);

    if (event === "CALL") {
      direction === "IN" ? (row.inCall += 1) : (row.outCall += 1);
      row.total += 1;
    } else if (event === "SMS") {
      direction === "IN" ? (row.inSms += 1) : (row.outSms += 1);
      row.total += 1;
    } else {
      row.total += 1;
    }
  };

  for (const r of normRows) {
    const bInfo = classifyBParty(r.bParty);
    const loc = r.location || r.siteLocation || "(blank)";
    const imei = r.imei || "(blank)";
    const a = r.aParty || "(blank)";
    const { direction, event } = classifyDirectionEvent(r.callType || "");

    // MFC buckets
    if (bInfo.type === "MSISDN") bump(mfcMap, bInfo.key, event, direction);
    else if (bInfo.type === "SHORTCODE") bump(shortCodeMap, bInfo.key, event, direction);
    else bump(invalidMap, bInfo.key, event, direction);

    // MFL
    if (!mflMap.has(loc)) mflMap.set(loc, { location: loc, inCall: 0, outCall: 0, inSms: 0, outSms: 0, total: 0 });
    const mfl = mflMap.get(loc);
    if (event === "CALL") {
      direction === "IN" ? (mfl.inCall += 1) : (mfl.outCall += 1);
      mfl.total += 1;
    } else if (event === "SMS") {
      direction === "IN" ? (mfl.inSms += 1) : (mfl.outSms += 1);
      mfl.total += 1;
    } else mfl.total += 1;

    // IMEI summary
    if (!imeiMap.has(imei)) imeiMap.set(imei, { imei, count: 0, from: null, to: null });
    const im = imeiMap.get(imei);
    im.count += 1;
    if (r.dateTime instanceof Date && !isNaN(r.dateTime.getTime())) {
      if (!im.from || r.dateTime < im.from) im.from = r.dateTime;
      if (!im.to || r.dateTime > im.to) im.to = r.dateTime;
    }

    // IMEI scan
    if (!imeiScanMap.has(imei)) imeiScanMap.set(imei, new Map());
    const sub = imeiScanMap.get(imei);
    sub.set(a, (sub.get(a) || 0) + 1);
  }

  const sortByTotal = (arr) => arr.sort((a, b) => b.total - a.total);

  const mfcAll = sortByTotal(Array.from(mfcMap.values()));
  const shortCodesAll = sortByTotal(Array.from(shortCodeMap.values()));
  const invalidAll = sortByTotal(Array.from(invalidMap.values()));

  const mflAll = Array.from(mflMap.values()).sort((a, b) => b.total - a.total);
  const imeiSummary = Array.from(imeiMap.values()).sort((a, b) => b.count - a.count);

  const imeiScan = Array.from(imeiScanMap.entries()).map(([imei, sub]) => {
    const topNumbers = Array.from(sub.entries())
      .map(([msisdn, count]) => ({ msisdn, count, guessedNetwork: guessOperatorByPrefix(msisdn) }))
      .sort((a, b) => b.count - a.count);
    return { imei, topNumbers };
  });

  return { mfcAll, shortCodesAll, invalidAll, mflAll, imeiSummary, imeiScan };
}

function SimpleTableWithFooter({ columns, rows, footer, emptyText = "No data" }) {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "auto",
        maxHeight: 520,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Table stickyHeader size="small" sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography variant="body2" color="text.secondary">
                  {emptyText}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r, idx) => (
              <TableRow key={r.__id ?? idx} hover>
                {columns.map((c) => (
                  <TableCell key={c.key} sx={{ whiteSpace: "nowrap" }}>
                    {c.render ? c.render(r) : toStr(r[c.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
        {footer && (
          <TableFooter>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  {footer[c.key] ?? ""}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function buildErrorReportText(validation, fileName, detectedByPrefix, detectedByHeaders) {
  const lines = [];
  lines.push("CDR Excel Format Validation Report");
  lines.push("================================");
  lines.push(`File: ${fileName || "-"}`);
  lines.push(`Prefix Guess: ${detectedByPrefix}`);
  lines.push(`Header Guess: ${detectedByHeaders}`);
  lines.push("");
  lines.push(`Detected Company Pattern: ${validation?.company || "-"}`);
  lines.push(`Status: ${validation?.ok ? "PASS" : "FAIL"}`);
  lines.push(`Expected Columns Count: ${validation?.expectedCount ?? 0}`);
  lines.push(`Found Columns Count: ${validation?.actualCount ?? 0}`);
  lines.push("");
  if (validation && !validation.ok) {
    lines.push("Missing Columns (exact):");
    lines.push(validation.missing.length ? " - " + validation.missing.join("\n - ") : " - None");
    lines.push("");
    lines.push("Extra Columns (not allowed):");
    lines.push(validation.extra.length ? " - " + validation.extra.join("\n - ") : " - None");
    lines.push("");
  }
  lines.push("Expected Columns:");
  lines.push(" - " + (validation?.expected || []).join("\n - "));
  lines.push("");
  lines.push("Actual Columns:");
  lines.push(" - " + (validation?.actualHeaders || []).join("\n - "));
  return lines.join("\n");
}

function addPdfBranding(doc, text = "https://www.cyberpunk.pk/") {
  const pageCount = doc.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    doc.saveGraphicsState();

    // ✅ Light / transparent watermark-like text
    doc.setTextColor(17, 24, 39); // #111827
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);

    // jsPDF supports opacity via GState (works in modern jsPDF builds)
    try {
      const gs = new doc.GState({ opacity: 0.18 }); // ✅ transparency
      doc.setGState(gs);
    } catch (e) {
      // If opacity not supported in your build, it will still print (no crash)
    }

    // Bottom-right position (with margin)
    const margin = 28;
    doc.text(text, pageW - margin, pageH - margin, {
      align: "right",
      baseline: "bottom",
    });

    doc.restoreGraphicsState();
  }
}


export default function CDRStrictExcelAnalyzer_FullMerged() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fileRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [rawHeaders, setRawHeaders] = useState([]);

  const [detectedByPrefix, setDetectedByPrefix] = useState("Unknown");
  const [detectedByHeaders, setDetectedByHeaders] = useState("Unknown");

  const [validation, setValidation] = useState(null);
  const [allValidation, setAllValidation] = useState([]);
  const [scientificWarning, setScientificWarning] = useState(null);

  const [processing, setProcessing] = useState(false);
  const [normRows, setNormRows] = useState([]);
  const [analysis, setAnalysis] = useState(null);

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [imeiFilter, setImeiFilter] = useState("");
  const [bPartySearch, setBPartySearch] = useState("");

  // Top N
  const [topN, setTopN] = useState(10);

  // UI flow
  const [selectedMode, setSelectedMode] = useState(null); // null | "single" | "multiple"
  const [stage, setStage] = useState("initial"); // "initial" | "fileSelected" | "processing" | "done"

  const [tab, setTab] = useState("Upload");

  const rawPreview = useMemo(() => rawRows.slice(0, 500), [rawRows]);
  const rawColumns = useMemo(() => rawHeaders.map((h) => ({ key: h, label: h })), [rawHeaders]);

  const pageStyle = useMemo(
    () => ({ ...styles.page, padding: isMobile ? 12 : 16 }),
    [isMobile]
  );
  const headingMainStyle = useMemo(
    () => ({ ...styles.headingMain, color: theme.palette.text.primary }),
    [theme]
  );
  const headingSectionStyle = useMemo(
    () => ({ ...styles.headingSection, color: theme.palette.text.primary }),
    [theme]
  );
  const mutedStyle = useMemo(
    () => ({ ...styles.muted, color: theme.palette.text.secondary }),
    [theme]
  );
  const okStyle = useMemo(
    () => ({ ...styles.ok, color: theme.palette.success.main }),
    [theme]
  );
  const labelStyle = useMemo(
    () => ({ ...styles.label, color: theme.palette.text.secondary }),
    [theme]
  );
  const listTextColor = useMemo(() => theme.palette.text.primary, [theme]);
  const rowStyle = useMemo(
    () => ({
      ...styles.row,
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "stretch" : "center",
    }),
    [isMobile]
  );
  const fieldStyle = useMemo(
    () => ({
      ...styles.field,
      minWidth: isMobile ? "100%" : 200,
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.paper,
    }),
    [isMobile, theme]
  );
  const inputStyle = useMemo(
    () => ({ ...styles.input, color: theme.palette.text.primary }),
    [theme]
  );
  const headerInfoStyle = useMemo(
    () => ({ flex: 1, minWidth: isMobile ? "100%" : 260 }),
    [isMobile]
  );

  const canAnalyze = validation?.ok && rawRows.length > 0 && !processing && selectedMode === "single";

  const tabs = useMemo(() => {
    const base = ["Upload", "Raw Data"];
    if (stage === "processing") return [...base, "Processing"];
    if (analysis && stage === "done") return [...base, "MFC", "Short Codes", "MFL", "IMEI", "IMEI Scan"];
    return base;
  }, [analysis, stage]);

  const filteredNormRows = useMemo(() => {
    if (!normRows.length) return [];
    const from = fromDate ? new Date(fromDate + "T00:00:00") : null;
    const to = toDate ? new Date(toDate + "T23:59:59") : null;
    const imeiQ = imeiFilter.trim();
    const bQ = bPartySearch.trim();

    return normRows.filter((r) => {
      if (from || to) {
        const dt = r.dateTime instanceof Date ? r.dateTime : null;
        if (!dt) return false;
        if (from && dt < from) return false;
        if (to && dt > to) return false;
      }
      if (imeiQ && !toStr(r.imei).includes(imeiQ)) return false;
      if (bQ && !toStr(r.bParty).includes(bQ)) return false;
      return true;
    });
  }, [normRows, fromDate, toDate, imeiFilter, bPartySearch]);

  const filteredAnalysis = useMemo(() => {
    if (!analysis) return null;
    return analyzeCDR(filteredNormRows);
  }, [analysis, filteredNormRows]);

  const mfcRows = useMemo(() => (filteredAnalysis ? filteredAnalysis.mfcAll.slice(0, topN) : []), [filteredAnalysis, topN]);
  const mflRows = useMemo(() => (filteredAnalysis ? filteredAnalysis.mflAll.slice(0, topN) : []), [filteredAnalysis, topN]);
  const scRows = useMemo(() => (filteredAnalysis ? (filteredAnalysis.shortCodesAll || []).slice(0, topN) : []), [filteredAnalysis, topN]);

  const mfcFooter = useMemo(() => {
    if (!filteredAnalysis) return null;
    const sums = filteredAnalysis.mfcAll.reduce(
      (acc, r) => {
        acc.inCall += r.inCall; acc.outCall += r.outCall; acc.inSms += r.inSms; acc.outSms += r.outSms; acc.total += r.total;
        return acc;
      },
      { inCall: 0, outCall: 0, inSms: 0, outSms: 0, total: 0 }
    );
    return { bParty: "Grand Total", ...sums };
  }, [filteredAnalysis]);

  const mflFooter = useMemo(() => {
    if (!filteredAnalysis) return null;
    const sums = filteredAnalysis.mflAll.reduce(
      (acc, r) => {
        acc.inCall += r.inCall; acc.outCall += r.outCall; acc.inSms += r.inSms; acc.outSms += r.outSms; acc.total += r.total;
        return acc;
      },
      { inCall: 0, outCall: 0, inSms: 0, outSms: 0, total: 0 }
    );
    return { location: "Grand Total", ...sums };
  }, [filteredAnalysis]);

  const errorReportText = useMemo(() => {
    if (!validation || validation.ok) return "";
    return buildErrorReportText(validation, fileName, detectedByPrefix, detectedByHeaders);
  }, [validation, fileName, detectedByPrefix, detectedByHeaders]);

  const copyErrorReport = async () => {
    if (!errorReportText) return;
    try {
      await navigator.clipboard.writeText(errorReportText);
      alert("Error report copied.");
    } catch {
      alert("Copy failed (permission). Please download instead.");
    }
  };

  const downloadErrorReport = () => {
    if (!errorReportText) return;
    downloadText(`CDR_Format_Error_Report_${Date.now()}.txt`, errorReportText);
  };

  const clearAnalysisState = () => {
    setFileName("");
    setRawRows([]);
    setRawHeaders([]);
    setDetectedByPrefix("Unknown");
    setDetectedByHeaders("Unknown");
    setValidation(null);
    setAllValidation([]);
    setScientificWarning(null);
    setAnalysis(null);
    setNormRows([]);
    setProcessing(false);
    setFromDate("");
    setToDate("");
    setImeiFilter("");
    setBPartySearch("");
    setTopN(10);
    setTab("Upload");
    if (fileRef.current) fileRef.current.value = "";
  };

  const selectMode = (mode) => {
    if (mode === selectedMode) return;
    setSelectedMode(mode);
    clearAnalysisState();
    setStage("initial");
  };

  const processSelectedFile = async (f) => {
    if (!f) return;
    if (!isSupportedDatasetFile(f)) {
      alert("Please select a supported file (.xls, .xlsx, .xlsm, .xlsb, .csv).");
      return;
    }

    setFileName(f.name);
    setProcessing(false);
    setAnalysis(null);
    setNormRows([]);
    setValidation(null);
    setAllValidation([]);
    setDetectedByPrefix("Unknown");
    setDetectedByHeaders("Unknown");
    setScientificWarning(null);
    setTab("Raw Data");
    setStage("fileSelected");

    setFromDate(""); setToDate(""); setImeiFilter(""); setBPartySearch(""); setTopN(10);

    let dataset;
    try {
      dataset = await loadDatasetFromFile(f);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to read file.");
      return;
    }

    const json = Array.isArray(dataset?.rows) ? dataset.rows : [];
    const headers = Array.isArray(dataset?.headers) ? dataset.headers : [];
    setRawRows(json);
    setRawHeaders(headers);
    setScientificWarning(getScientificWarningInfo(json, headers));

    const hdrDetect = detectCompanyByHeaders(headers);
    setDetectedByHeaders(hdrDetect.detected);
    setAllValidation(hdrDetect.results);

    // prefix guess
    let prefixGuess = "Unknown";
    const tryCols = ["MSISDN", "Aparty", "A Number", "call_org_num"];
    for (const col of tryCols) {
      if (headers.includes(col)) {
        for (const r of json) {
          const v = normalizeMSISDN(r[col]);
          if (v) { prefixGuess = guessOperatorByPrefix(v); break; }
        }
      }
      if (prefixGuess !== "Unknown") break;
    }
    setDetectedByPrefix(prefixGuess);

    const chosen = hdrDetect.detected === "Unknown" ? "Unknown" : hdrDetect.detected;
    if (chosen !== "Unknown") {
      setValidation(validateAgainstPattern(headers, chosen));
    } else {
      setValidation({
        ok: false, company: "Unknown", expectedCount: 0, actualCount: headers.length,
        missing: [], extra: headers, expected: [], actualHeaders: headers,
      });
    }
  };

  const pickFile = async () => {
    // Use the native file picker with explicit Excel type label when available.
    if (window.showOpenFilePicker) {
      try {
        const [handle] = await window.showOpenFilePicker({
          multiple: false,
          types: [
            {
              description: "CDR Data Files",
              accept: {
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                "application/vnd.ms-excel": [".xls"],
                "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
                "application/vnd.ms-excel.sheet.binary.macroEnabled.12": [".xlsb"],
                "text/csv": [".csv"],
              },
            },
          ],
          excludeAcceptAllOption: true,
        });
        const f = await handle.getFile();
        await processSelectedFile(f);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }

    if (fileRef.current) {
      fileRef.current.accept = EXCEL_ACCEPT;
      fileRef.current.click();
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    await processSelectedFile(f);
  };

  const handleAnalyze = async () => {
    if (!validation?.ok) {
      setTab("Upload");
      setStage("fileSelected");
      return;
    }
    setProcessing(true);
    setStage("processing");
    setTab("Processing");

    await new Promise((r) => setTimeout(r, 900));
    const normalized = buildNormalizedRowsByCompany(rawRows, validation.company);
    setNormRows(normalized);

    await new Promise((r) => setTimeout(r, 700));
    setAnalysis(analyzeCDR(normalized)); // keeps "analysis" truthy (UI gates)
    setProcessing(false);
    setStage("done");
    setTab("MFC");
  };

  const reset = () => {
    clearAnalysisState();
    setStage("initial");
  };

  const exportToExcel = () => {
    if (!filteredAnalysis) return;
    const wb = XLSX.utils.book_new();

    const mfcSheetData = [
      ["B Party","Incoming Call","Outgoing Call","Incoming SMS","Outgoing SMS","Grand Total"],
      ...filteredAnalysis.mfcAll.map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
      [],
      ["Grand Total", mfcFooter?.inCall ?? 0, mfcFooter?.outCall ?? 0, mfcFooter?.inSms ?? 0, mfcFooter?.outSms ?? 0, mfcFooter?.total ?? 0],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(mfcSheetData), "MFC");

    // ✅ ShortCodes sheet added (non-breaking)
    const scData = [
      ["Short Code","Incoming Call","Outgoing Call","Incoming SMS","Outgoing SMS","Grand Total"],
      ...(filteredAnalysis.shortCodesAll || []).map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(scData.length ? scData : [["No data"]]), "ShortCodes");

    const mflSheetData = [
      ["Location","Incoming Call","Incoming SMS","Outgoing Call","Outgoing SMS","Grand Total"],
      ...filteredAnalysis.mflAll.map((r) => [r.location, r.inCall, r.inSms, r.outCall, r.outSms, r.total]),
      [],
      ["Grand Total", mflFooter?.inCall ?? 0, mflFooter?.inSms ?? 0, mflFooter?.outCall ?? 0, mflFooter?.outSms ?? 0, mflFooter?.total ?? 0],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(mflSheetData), "MFL");

    const imeiSheetData = [
      ["IMEI","Count (Records)","From","To"],
      ...filteredAnalysis.imeiSummary.map((r) => [r.imei, r.count, r.from ? r.from.toLocaleString() : "", r.to ? r.to.toLocaleString() : ""]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(imeiSheetData), "IMEI");

    const scanRows = [];
    filteredAnalysis.imeiScan.forEach((block) => {
      scanRows.push(["IMEI", block.imei, "", ""]);
      scanRows.push(["Mob No","Network (prefix)","Uses",""]);
      block.topNumbers.forEach((n) => scanRows.push([n.msisdn, n.guessedNetwork, n.count, ""]));
      scanRows.push(["", "", "", ""]);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(scanRows.length ? scanRows : [["No data"]]), "IMEI_Scan");

    XLSX.writeFile(wb, `CDR_Analysis_${Date.now()}.xlsx`);
  };

  /**
   * ✅ PDF tuned:
   * - Respects TopN (no huge repeated pages)
   * - Totals show only once per section (showFoot: "lastPage")
   * - Adds headings (MFC, Short Codes, MFL, IMEI)
   */
  const exportToPDF = () => {
    if (!filteredAnalysis) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

    doc.setFontSize(14);
    doc.text(`CDR Analysis Report (${validation?.company || "Unknown"})`, 40, 40);

    doc.setFontSize(10);
    doc.text(
      [
        `File: ${fileName || "-"}`,
        `Filters: ${fromDate || "-"} to ${toDate || "-"} | IMEI: ${imeiFilter || "-"} | B-Party: ${bPartySearch || "-"}`,
        `Top: ${topN} | Generated: ${new Date().toLocaleString()}`,
      ],
      40,
      60
    );

    // MFC
    doc.setFontSize(12);
    doc.text(`MFC — Most Frequent Contacts (Top ${topN})`, 40, 95);
    autoTable(doc, {
      startY: 105,
      head: [["B Party","In Call","Out Call","In SMS","Out SMS","Total"]],
      body: (filteredAnalysis.mfcAll || []).slice(0, topN).map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
      foot: [[ "Grand Total", mfcFooter?.inCall ?? 0, mfcFooter?.outCall ?? 0, mfcFooter?.inSms ?? 0, mfcFooter?.outSms ?? 0, mfcFooter?.total ?? 0 ]],
      showFoot: "lastPage",
      styles: { fontSize: 8 },
      theme: "grid",
    });

    // Short Codes
    doc.setFontSize(12);
    doc.text(`Short Codes / Service IDs (Top ${topN})`, 40, doc.lastAutoTable.finalY + 18);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 28,
      head: [["Short Code","In Call","Out Call","In SMS","Out SMS","Total"]],
      body: (filteredAnalysis.shortCodesAll || []).slice(0, topN).map((r) => [r.bParty, r.inCall, r.outCall, r.inSms, r.outSms, r.total]),
      styles: { fontSize: 8 },
      theme: "grid",
    });

    // MFL
    doc.setFontSize(12);
    doc.text(`MFL — Most Frequent Locations (Top ${topN})`, 40, doc.lastAutoTable.finalY + 18);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 28,
      head: [["Location","In Call","In SMS","Out Call","Out SMS","Total"]],
      body: (filteredAnalysis.mflAll || []).slice(0, topN).map((r) => [r.location, r.inCall, r.inSms, r.outCall, r.outSms, r.total]),
      foot: [[ "Grand Total", mflFooter?.inCall ?? 0, mflFooter?.inSms ?? 0, mflFooter?.outCall ?? 0, mflFooter?.outSms ?? 0, mflFooter?.total ?? 0 ]],
      showFoot: "lastPage",
      styles: { fontSize: 8 },
      theme: "grid",
    });

    // IMEI
    doc.setFontSize(12);
    doc.text("IMEI Summary", 40, doc.lastAutoTable.finalY + 18);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 28,
      head: [["IMEI","Count","From","To"]],
      body: (filteredAnalysis.imeiSummary || []).map((r) => [
        r.imei,
        r.count,
        r.from ? r.from.toLocaleString() : "",
        r.to ? r.to.toLocaleString() : "",
      ]),
      styles: { fontSize: 8 },
      theme: "grid",
    });

   addPdfBranding(doc, "https://www.cyberpunk.pk/");
    doc.save(`CDR_Analysis_${Date.now()}.pdf`);
  };

  return (
    <div style={pageStyle}>
      <input ref={fileRef} type="file" accept={EXCEL_ACCEPT} style={{ display: "none" }} onChange={handleFile} />

      <SurfaceCard>
        <div style={{ fontSize: 18, fontWeight: 900 }}>CDR Analyzer</div>
        <div style={mutedStyle}>Select an analysis mode.</div>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          <AppButton
            tone={selectedMode === "single" ? "primary" : "ghost"}
            onClick={() => selectMode("single")}
          >
            Single CDR Analysis
          </AppButton>
          <AppButton
            tone={selectedMode === "multiple" ? "primary" : "ghost"}
            onClick={() => selectMode("multiple")}
          >
            Multiple CDR Analysis
          </AppButton>
        </Box>
      </SurfaceCard>

      {selectedMode === null && (
        <SurfaceCard>
          <div style={mutedStyle}>Choose an option above to continue.</div>
        </SurfaceCard>
      )}

      {selectedMode === "multiple" && (
        <SurfaceCard>
          <h2 style={headingSectionStyle}>Multiple CDR Analysis</h2>
          <p style={mutedStyle}>Coming soon.</p>
        </SurfaceCard>
      )}

      {selectedMode === "single" && (
        <>
          {stage === "done" && (
            <>
              <h1 style={headingMainStyle}>CDR Analyzer Tool – Call Detail Record Analysis Online</h1>
              <p style={mutedStyle}>
                Upload Excel-based CDRs (Jazz, Zong, Ufone, Telenor) and generate MFC, MFL, IMEI Summary and IMEI Scan reports.
              </p>
            </>
          )}

          {stage === "initial" && (
            <SurfaceCard>
              <div style={styles.centerWrap}>
                <div>
                  <AppButton onClick={pickFile}>Upload CDR</AppButton>
                  <div style={{ marginTop: 10, ...mutedStyle }}>Accepted formats: .xls, .xlsx, .xlsm, .xlsb, .csv</div>
                </div>
              </div>
            </SurfaceCard>
          )}

          {stage !== "initial" && (
            <SurfaceCard>
              <div style={rowStyle}>
                <div style={headerInfoStyle}>
                  <div style={{ fontSize: 18, fontWeight: 900 }}>CDR Strict Analyzer</div>
                  <div style={mutedStyle}>Strict header/count checks with diagnostics, error report export, and raw-data-safe workflow.</div>
                </div>

                <AppBadge label={`Prefix Guess: ${detectedByPrefix}`} />
                <AppBadge label={`Header Guess: ${detectedByHeaders}`} />

                <AppButton tone="ghost" onClick={pickFile}>{fileName ? "Replace CDR" : "Upload CDR"}</AppButton>

                <AppButton sx={{ opacity: canAnalyze ? 1 : 0.5 }} disabled={!canAnalyze} onClick={handleAnalyze}>
                  {processing ? "Analyzing..." : "Submit & Analyze"}
                </AppButton>

                <AppButton tone="ghost" onClick={reset}>Reset</AppButton>
              </div>

              <div style={{ marginTop: 10 }}>
                {fileName ? <span style={okStyle}>Attached: {fileName}</span> : <span style={mutedStyle}>No file yet.</span>}
              </div>

              {scientificWarning && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                  Scientific ID format detected in <b>{scientificWarning.column}</b> (example: <b>{scientificWarning.sample}</b>).
                  Analyzer will normalize this for calculations. If the source export already rounded values, exact original digits cannot be recovered.
                </Alert>
              )}

              {validation && !validation.ok && (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                  <div style={{ ...styles.danger, marginBottom: 4 }}>❌ Format mismatch for detected company: <b>{validation.company}</b></div>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    <div><b>Column count mismatch:</b> Expected <b>{validation.expectedCount}</b>, Found <b>{validation.actualCount}</b></div>

                    {validation.missing.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <b>Missing columns (exact):</b>
                        <div style={mutedStyle}>{validation.missing.join(", ")}</div>
                      </div>
                    )}

                    {validation.extra.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <b>Extra columns (not allowed):</b>
                        <div style={mutedStyle}>{validation.extra.join(", ")}</div>
                      </div>
                    )}

                    <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <AppButton tone="danger" onClick={copyErrorReport}>Copy Error Report</AppButton>
                      <AppButton tone="danger" onClick={downloadErrorReport}>Download Error Report (.txt)</AppButton>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 900, fontSize: 13 }}>Expected columns for {validation.company}:</div>
                      <div style={mutedStyle}>{(PATTERNS[validation.company] || []).join(" | ")}</div>
                    </div>
                  </div>
                </Alert>
              )}

              {allValidation.length > 0 && (!validation || !validation.ok) && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontWeight: 900, marginBottom: 6, fontSize: 13 }}>Pattern match diagnostics</div>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      overflowX: "auto",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            Company Pattern
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            Expected
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            Found
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            Missing
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            Extra
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allValidation.slice(0, 4).map((r, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.company}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.expectedCount}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.actualCount}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.missing.length}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.extra.length}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <div style={{ marginTop: 8, ...mutedStyle }}>Shows exactly which operator template is failing and why.</div>
                </div>
              )}

              {validation && validation.ok && (
                <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>
                  ✅ Format OK for: {validation.company}. You can run analysis.
                </Alert>
              )}
            </SurfaceCard>
          )}

          {stage === "processing" && (
            <SurfaceCard>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <CircularProgress size={18} thickness={5} />
                <div>
                  <div style={{ fontWeight: 900 }}>Analyzing…</div>
                  <div style={mutedStyle}>Strict parsing + grouping contacts/short-codes/locations/IMEI.</div>
                </div>
              </div>
            </SurfaceCard>
          )}

          {(stage === "fileSelected" || stage === "done") && (
            <>
              {stage === "done" && analysis && (
                <SurfaceCard>
                  <div style={{ fontWeight: 900, marginBottom: 10 }}>Filters & Exports</div>
                  <div style={rowStyle}>
                    <div style={fieldStyle}>
                      <div style={labelStyle}>From Date</div>
                      <input style={inputStyle} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </div>
                    <div style={fieldStyle}>
                      <div style={labelStyle}>To Date</div>
                      <input style={inputStyle} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </div>
                    <div style={fieldStyle}>
                      <div style={labelStyle}>IMEI Filter (contains)</div>
                      <input style={inputStyle} value={imeiFilter} onChange={(e) => setImeiFilter(e.target.value)} placeholder="e.g., 358..." />
                    </div>
                    <div style={fieldStyle}>
                      <div style={labelStyle}>B-Party Search (contains)</div>
                      <input style={inputStyle} value={bPartySearch} onChange={(e) => setBPartySearch(e.target.value)} placeholder="e.g., 0335..." />
                    </div>

                    <div style={fieldStyle}>
                      <div style={labelStyle}>Top Rows</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <AppButton tone={topN === 10 ? "primary" : "ghost"} onClick={() => setTopN(10)}>Top 10</AppButton>
                        <AppButton tone={topN === 20 ? "primary" : "ghost"} onClick={() => setTopN(20)}>Top 20</AppButton>
                      </div>
                    </div>

                    <AppButton onClick={exportToExcel}>Export Excel</AppButton>
                    <AppButton onClick={exportToPDF}>Export PDF</AppButton>
                    <AppBadge label={`Filtered rows: ${filteredNormRows.length}`} />
                  </div>

                  <div style={{ marginTop: 10, ...mutedStyle }}>
                    Tables reflect filters. Grand Totals are totals of the full filtered dataset.
                  </div>
                </SurfaceCard>
              )}

              <SurfaceCard>
                <div style={styles.tabs}>
                  {tabs.map((t) => (
                    <div key={t} style={styles.tab(tab === t)} onClick={() => setTab(t)}>{t}</div>
                  ))}
                </div>

                <div style={{ marginTop: 12 }}>
                  {tab === "Upload" && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8, color: listTextColor }}>Upload</div>
                      <div style={{ ...mutedStyle, marginBottom: 10 }}>
                        {fileName ? `Attached: ${fileName}` : "No file attached."}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                        <AppButton tone="ghost" onClick={pickFile}>{fileName ? "Replace CDR" : "Upload CDR"}</AppButton>
                        <AppButton sx={{ opacity: canAnalyze ? 1 : 0.5 }} disabled={!canAnalyze} onClick={handleAnalyze}>
                          {processing ? "Analyzing..." : "Submit & Analyze"}
                        </AppButton>
                        <AppButton tone="ghost" onClick={reset}>Reset</AppButton>
                      </div>
                      <div style={{ fontWeight: 900, color: listTextColor }}>Rules</div>
                      <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, fontSize: 14, color: listTextColor }}>
                        <li>Strict templates: exact column names + exact column count.</li>
                        <li>If mismatch: exact error + missing/extra + downloadable error report.</li>
                        <li>Raw Excel preview always visible.</li>
                        <li>MFC dedupe: 0092/92/03 variants merged. Short codes separated.</li>
                      </ul>
                    </div>
                  )}

                  {tab === "Processing" && (
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <CircularProgress size={18} thickness={5} />
                      <div>
                        <div style={{ fontWeight: 900 }}>Analyzing…</div>
                        <div style={mutedStyle}>Strict parsing + grouping contacts/short-codes/locations/IMEI.</div>
                      </div>
                    </div>
                  )}

                  {tab === "Raw Data" && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Raw Excel View (first {rawPreview.length} rows)</div>
                      <SimpleTableWithFooter columns={rawColumns} rows={rawPreview} emptyText="Upload an Excel to preview raw data." />
                      {rawRows.length > 500 && <div style={{ marginTop: 8, ...mutedStyle }}>Showing first 500 rows for UI performance.</div>}
                    </div>
                  )}

                  {tab === "MFC" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>MFC (Most Frequent Contacts) — Showing {topN}</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "bParty", label: "B Party (Canonical)" },
                          { key: "inCall", label: "Incoming Call" },
                          { key: "outCall", label: "Outgoing Call" },
                          { key: "inSms", label: "Incoming SMS" },
                          { key: "outSms", label: "Outgoing SMS" },
                          { key: "total", label: "Grand Total" },
                        ]}
                        rows={mfcRows.map((r, i) => ({ ...r, __id: i }))}
                        footer={mfcFooter}
                        emptyText="No data (check filters)."
                      />
                    </div>
                  )}

                  {tab === "Short Codes" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Short Codes / Service IDs — Showing {topN}</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "bParty", label: "Short Code" },
                          { key: "inCall", label: "Incoming Call" },
                          { key: "outCall", label: "Outgoing Call" },
                          { key: "inSms", label: "Incoming SMS" },
                          { key: "outSms", label: "Outgoing SMS" },
                          { key: "total", label: "Grand Total" },
                        ]}
                        rows={scRows.map((r, i) => ({ ...r, __id: i }))}
                        emptyText="No short codes (check filters)."
                      />
                      <div style={{ marginTop: 8, ...mutedStyle }}>
                        Note: short codes are separated to avoid inflating MFC contacts.
                      </div>
                    </div>
                  )}

                  {tab === "MFL" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>MFL (Most Frequent Locations) — Showing {topN}</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "location", label: "Location" },
                          { key: "inCall", label: "Incoming Call" },
                          { key: "inSms", label: "Incoming SMS" },
                          { key: "outCall", label: "Outgoing Call" },
                          { key: "outSms", label: "Outgoing SMS" },
                          { key: "total", label: "Grand Total" },
                        ]}
                        rows={mflRows.map((r, i) => ({ ...r, __id: i }))}
                        footer={mflFooter}
                        emptyText="No data (check filters)."
                      />
                    </div>
                  )}

                  {tab === "IMEI" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>IMEI Summary</div>
                      <SimpleTableWithFooter
                        columns={[
                          { key: "imei", label: "IMEI" },
                          { key: "count", label: "Count (Records)" },
                          { key: "from", label: "From", render: (r) => (r.from ? r.from.toLocaleString() : "") },
                          { key: "to", label: "To", render: (r) => (r.to ? r.to.toLocaleString() : "") },
                        ]}
                        rows={filteredAnalysis.imeiSummary.map((r, i) => ({ ...r, __id: i }))}
                        emptyText="No IMEI values (check filters)."
                      />
                    </div>
                  )}

                  {tab === "IMEI Scan" && filteredAnalysis && (
                    <div>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>IMEI Scan</div>
                      <div style={{ display: "grid", gap: 12 }}>
                        {filteredAnalysis.imeiScan.map((block, idx) => (
                          <div key={block.imei + idx} style={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 12, padding: 12 }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                              <div style={{ fontWeight: 900 }}>IMEI: {block.imei}</div>
                              <AppBadge label={`Top Numbers shown: ${Math.min(15, block.topNumbers.length)}`} />
                            </div>
                            <div style={{ marginTop: 10 }}>
                              <SimpleTableWithFooter
                                columns={[
                                  { key: "msisdn", label: "Mob No" },
                                  { key: "guessedNetwork", label: "Network (by prefix)" },
                                  { key: "count", label: "Uses" },
                                ]}
                                rows={block.topNumbers.slice(0, 15).map((r, i) => ({ ...r, __id: `${idx}-${i}` }))}
                                emptyText="No numbers for this IMEI."
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!filteredAnalysis && (tab === "MFC" || tab === "Short Codes" || tab === "MFL" || tab === "IMEI" || tab === "IMEI Scan") && (
                    <div style={mutedStyle}>Analysis not generated yet (or disabled due to format mismatch).</div>
                  )}
                </div>
              </SurfaceCard>
            </>
          )}

          {stage === "done" && (
            <SurfaceCard>
              <h2 style={headingSectionStyle}>What is a CDR Analyzer?</h2>
              <p style={mutedStyle}>
                A CDR Analyzer tool helps investigators and analysts examine Call Detail Records (CDR) to identify frequent contacts,
                call patterns, IMEI usage, and location history. This tool validates operator-specific Excel formats and generates
                investigation-ready summaries.
              </p>

              <h2 style={headingSectionStyle}>Supported Telecom Operators</h2>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, color: listTextColor }}>
                <li>Jazz CDR Excel</li>
                <li>Zong CDR Excel</li>
                <li>Ufone CDR Excel</li>
                <li>Telenor CDR Excel</li>
              </ul>

              <h2 style={headingSectionStyle}>Key Features</h2>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, color: listTextColor }}>
                <li>Strict column validation with downloadable error report</li>
                <li>Filters: date range, IMEI filter, B-party search</li>
                <li>Top 10 / Top 20 toggle and Grand Totals</li>
                <li>Export results to Excel and PDF</li>
              </ul>
            </SurfaceCard>
          )}
        </>
      )}
    </div>
  );
}
